import InventoryApi from '@/api/InventoryApi';
import { InventoryManagementApi } from '@/api/InventoryManagementApi';
import AudioUtil, { soundNames } from '@/utils/AudioUtil';

export const mutationTypes = {
    INCREMENT_TOTAL_IN_FLIGHT: 'INCREMENT_TOTAL_IN_FLIGHT',
    SET_ACTIVE_SHELF: 'SET_ACTIVE_SHELF',
    SET_SHELF_ITEMS: 'SET_SHELF_ITEMS',
    RESET_TOTAL_IN_FLIGHT: 'RESET_TOTAL_IN_FLIGHT',
};

export default {
    namespaced: true,
    state: {
        /**
        * @type {import('@/typedefs/inventory/shelfLocator').ShelfLocator}
        */
        activeShelf: {},
        /**
         * @type {import('@/typedefs/inventory/shelfItem').ShelfItem[]}
         */
        shelfItems: [],

        // Since multiple requests are frequently outstanding simultaneously,
        // we will keep track of total outstanding requests rather than a boolean here
        /**
         * @type {Number}
         */
        totalInFlight: 0,
    },
    getters: {
        hasActiveShelf: ( state ) => Object.keys( state.activeShelf || {} ).length > 0,
        scannedItems: ( state ) => state.shelfItems.filter( ( shelfItem ) => shelfItem.physicalInventoryScanned ),
        inFlight: ( state ) => state.totalInFlight > 0,
    },
    actions: {
        reset( { commit } ) {
            commit( mutationTypes.RESET_TOTAL_IN_FLIGHT );
            commit( mutationTypes.SET_ACTIVE_SHELF, { shelf: {} } );
            commit( mutationTypes.SET_SHELF_ITEMS, { shelfItems: [] } );
        },

        async getShelfByBarcode( context, { barcode } ) {
            context.commit( mutationTypes.INCREMENT_TOTAL_IN_FLIGHT, { amount: 1 } );
            try {
                const { data } = await InventoryApi.getShelfLocatorInformation( {
                    jwtToken: context.rootState.User.jwtToken,
                    websiteId: context.rootGetters[ 'Website/id' ],
                    userAccountId: context.rootState.User.accountId,
                    locatorRef: barcode,
                } );

                context.commit(
                    mutationTypes.SET_ACTIVE_SHELF,
                    { shelf: data.locator || {} },
                );
                return data.locator;
            } finally {
                context.commit( mutationTypes.INCREMENT_TOTAL_IN_FLIGHT, { amount: -1 } );
            }
        },
        async getShelfItemsByBarcode(context, { barcode }) {
            const Api = new InventoryManagementApi(context.rootState.User.baseAuth, context.rootGetters['Website/locationId']);
            context.commit(mutationTypes.INCREMENT_TOTAL_IN_FLIGHT, { amount: 1 });
            const response = await Api.getSerialItems(barcode);

            if (response instanceof Error) {
                context.commit(mutationTypes.INCREMENT_TOTAL_IN_FLIGHT, { amount: -1 });
                await context.dispatch('setErrorNotification', { message: `Failed to get serial items - ${response.message}` } );
                return [];
            }

            context.commit(
                mutationTypes.SET_SHELF_ITEMS,
                { shelfItems: response || {} },
            );

            context.commit(mutationTypes.INCREMENT_TOTAL_IN_FLIGHT, { amount: -1 });
            return response;
        },

        async updateSerialItemLocator(context, { barcode, locatorRef }) {
            const Api = new InventoryManagementApi(context.rootState.User.baseAuth, context.rootGetters['Website/locationId']);
            context.commit('INCREMENT_TOTAL_IN_FLIGHT', { amount: 1 });
            const response = await Api.updateSerialItemLocator(barcode, locatorRef);

            if (response instanceof Error) {
                context.commit('INCREMENT_TOTAL_IN_FLIGHT', { amount: -1 });
                await context.dispatch('setErrorNotification', { message: `Failed to update serial item locator - ${ response.message }` } );
                throw response;
            }

            context.commit('INCREMENT_TOTAL_IN_FLIGHT', { amount: -1 });
            return response[0];
        },
        async setErrorNotification(context, { message }) {
            await context.dispatch( 'snackbar', {
                message,
                type: 'error',
                duration: 5000,
            }, { root: true } );
            await AudioUtil.playSound( soundNames.FAILURE_LONG );
        },
        setActiveShelf(context, { shelf }) {
            const newActiveShelf = !shelf ? {} : shelf;
            context.commit( mutationTypes.SET_ACTIVE_SHELF, { shelf: newActiveShelf } );
        },
        setShelfItems(context, { shelfItems }) {
            const newShelfItems = !shelfItems ? [] : shelfItems;
            context.commit( mutationTypes.SET_SHELF_ITEMS, { shelfItems: newShelfItems } );
        },
        markShelfItemScanned( context, { shelfItem } ) {
            const newShelfItem = { ...shelfItem, physicalInventoryScanned: true };
            const newShelfItems = [...context.state.shelfItems];

            const index = newShelfItems.findIndex(
                (item) => item.ref === newShelfItem.ref,
            );

            if (index === -1) {
                newShelfItems.push(newShelfItem);
            } else {
                newShelfItems[index] = newShelfItem;
            }

            context.commit(
                mutationTypes.SET_SHELF_ITEMS,
                { shelfItems: newShelfItems },
            );
        },
    },
    mutations: {
        [ mutationTypes.RESET_TOTAL_IN_FLIGHT ]( state ) {
            state.totalInFlight = 0;
        },
        [ mutationTypes.INCREMENT_TOTAL_IN_FLIGHT ]( state, { amount } ) {
            state.totalInFlight += amount;
        },
        [ mutationTypes.SET_ACTIVE_SHELF ]( state, { shelf } ) {
            state.activeShelf = shelf;
        },
        [ mutationTypes.SET_SHELF_ITEMS ]( state, { shelfItems } ) {
            state.shelfItems = shelfItems;
        },
    },
};
